import { useState, useEffect } from 'react';
import {
    Users,
    Building2,
    Shield,
    Activity,
    Settings,
    Search,
    MoreVertical,
    UserPlus,
    Ban,
    CheckCircle,
    AlertTriangle,
    TrendingUp,
    Clock,
} from 'lucide-react';
import Layout from '../components/shared/Layout';
import { apiGet, apiPost, apiPut, apiDelete } from '../config/api';
import { useAuth } from '../hooks/useAuth';

interface User {
    id: string;
    email: string;
    fullName: string;
    role: 'ADMIN' | 'MANAGER' | 'USER';
    createdAt: string;
    emailVerified: boolean;
}

interface PlatformStats {
    totalUsers: number;
    totalFacilities: number;
    totalEquipment: number;
    activeUsers24h: number;
    newUsersThisWeek: number;
    complianceRate: number;
}

export default function AdminDashboard() {
    const { user } = useAuth();
    const [users, setUsers] = useState<User[]>([]);
    const [stats, setStats] = useState<PlatformStats>({
        totalUsers: 0,
        totalFacilities: 0,
        totalEquipment: 0,
        activeUsers24h: 0,
        newUsersThisWeek: 0,
        complianceRate: 0,
    });
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [showRoleModal, setShowRoleModal] = useState(false);

    // Fetch users and stats
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch users
                const usersRes = await apiGet('/api/admin/users');
                if (usersRes.ok) {
                    const data = await usersRes.json();
                    setUsers(data.users || []);
                }

                // Fetch platform stats
                const statsRes = await apiGet('/api/admin/stats');
                if (statsRes.ok) {
                    const data = await statsRes.json();
                    setStats(data.stats || stats);
                }
            } catch (error) {
                console.error('Failed to fetch admin data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Filter users by search
    const filteredUsers = users.filter(
        u =>
            u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            u.fullName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Change user role
    const handleRoleChange = async (userId: string, newRole: 'ADMIN' | 'MANAGER' | 'USER') => {
        try {
            const res = await apiPut(`/api/admin/users/${userId}/role`, { role: newRole });
            if (res.ok) {
                setUsers(users.map(u => (u.id === userId ? { ...u, role: newRole } : u)));
                setShowRoleModal(false);
                setSelectedUser(null);
            }
        } catch (error) {
            console.error('Failed to update role:', error);
        }
    };

    // Delete user
    const handleDeleteUser = async (userId: string) => {
        if (!confirm('Are you sure you want to delete this user?')) return;
        try {
            const res = await apiDelete(`/api/admin/users/${userId}`);
            if (res.ok) {
                setUsers(users.filter(u => u.id !== userId));
            }
        } catch (error) {
            console.error('Failed to delete user:', error);
        }
    };

    const getRoleBadgeColor = (role: string) => {
        switch (role) {
            case 'ADMIN':
                return 'bg-red-100 text-red-700 border-red-200';
            case 'MANAGER':
                return 'bg-purple-100 text-purple-700 border-purple-200';
            default:
                return 'bg-steel-100 text-steel-700 border-steel-200';
        }
    };

    return (
        <Layout>
            <div className="min-h-screen p-8 bg-steel-50">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                            <Shield className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-display font-bold text-charcoal-950">
                                Admin Dashboard
                            </h1>
                            <p className="text-steel-600">
                                Manage users, monitor platform health
                            </p>
                        </div>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <StatCard
                        icon={Users}
                        label="Total Users"
                        value={stats.totalUsers || users.length}
                        trend="+12%"
                        color="blue"
                    />
                    <StatCard
                        icon={Building2}
                        label="Total Facilities"
                        value={stats.totalFacilities}
                        trend="+8%"
                        color="green"
                    />
                    <StatCard
                        icon={Activity}
                        label="Active Today"
                        value={stats.activeUsers24h}
                        trend="+5%"
                        color="purple"
                    />
                    <StatCard
                        icon={TrendingUp}
                        label="Compliance Rate"
                        value={`${stats.complianceRate}%`}
                        trend="+2%"
                        color="amber"
                    />
                </div>

                {/* Users Table */}
                <div className="card-glass p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <Users className="w-6 h-6 text-ember-500" />
                            <h2 className="text-xl font-display font-bold text-charcoal-950">
                                User Management
                            </h2>
                        </div>

                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-steel-400" />
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="pl-10 pr-4 py-2 bg-white border border-steel-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ember-500/20 focus:border-ember-500"
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-steel-200">
                                    <th className="text-left py-3 px-4 text-sm font-medium text-steel-600">User</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-steel-600">Role</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-steel-600">Status</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-steel-600">Joined</th>
                                    <th className="text-right py-3 px-4 text-sm font-medium text-steel-600">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="py-12 text-center text-steel-500">
                                            {loading ? 'Loading users...' : 'No users found'}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredUsers.map(u => (
                                        <tr key={u.id} className="border-b border-steel-100 hover:bg-steel-50/50 transition-colors">
                                            <td className="py-4 px-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-ember-400 to-ember-600 flex items-center justify-center text-white font-bold">
                                                        {u.fullName.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-charcoal-900">{u.fullName}</p>
                                                        <p className="text-sm text-steel-500">{u.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4">
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(u.role)}`}>
                                                    {u.role}
                                                </span>
                                            </td>
                                            <td className="py-4 px-4">
                                                {u.emailVerified ? (
                                                    <span className="flex items-center gap-1 text-sm text-safety-600">
                                                        <CheckCircle className="w-4 h-4" />
                                                        Verified
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-1 text-sm text-alert-600">
                                                        <AlertTriangle className="w-4 h-4" />
                                                        Pending
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-4 px-4">
                                                <span className="flex items-center gap-1 text-sm text-steel-600">
                                                    <Clock className="w-4 h-4" />
                                                    {new Date(u.createdAt).toLocaleDateString()}
                                                </span>
                                            </td>
                                            <td className="py-4 px-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedUser(u);
                                                            setShowRoleModal(true);
                                                        }}
                                                        className="p-2 hover:bg-steel-100 rounded-lg transition-colors"
                                                        title="Change role"
                                                    >
                                                        <Settings className="w-4 h-4 text-steel-600" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteUser(u.id)}
                                                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Delete user"
                                                        disabled={u.id === user?.id}
                                                    >
                                                        <Ban className="w-4 h-4 text-red-500" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Role Change Modal */}
                {showRoleModal && selectedUser && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl animate-scaleUp">
                            <h3 className="text-xl font-display font-bold text-charcoal-950 mb-4">
                                Change Role
                            </h3>
                            <p className="text-steel-600 mb-6">
                                Update role for <strong>{selectedUser.fullName}</strong>
                            </p>

                            <div className="space-y-3">
                                {(['USER', 'MANAGER', 'ADMIN'] as const).map(role => (
                                    <button
                                        key={role}
                                        onClick={() => handleRoleChange(selectedUser.id, role)}
                                        className={`w-full p-4 rounded-xl border-2 text-left transition-all ${selectedUser.role === role
                                                ? 'border-ember-500 bg-ember-50'
                                                : 'border-steel-200 hover:border-ember-300'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium text-charcoal-900">{role}</p>
                                                <p className="text-sm text-steel-500">
                                                    {role === 'ADMIN' && 'Full system access'}
                                                    {role === 'MANAGER' && 'Manage facilities and users'}
                                                    {role === 'USER' && 'Standard user access'}
                                                </p>
                                            </div>
                                            {selectedUser.role === role && (
                                                <CheckCircle className="w-5 h-5 text-ember-500" />
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>

                            <div className="mt-6 flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowRoleModal(false);
                                        setSelectedUser(null);
                                    }}
                                    className="flex-1 py-3 px-4 bg-steel-100 text-steel-700 rounded-xl font-medium hover:bg-steel-200 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

// Stat Card Component
function StatCard({
    icon: Icon,
    label,
    value,
    trend,
    color,
}: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    value: number | string;
    trend: string;
    color: 'blue' | 'green' | 'purple' | 'amber';
}) {
    const colorClasses = {
        blue: 'from-blue-500 to-blue-600',
        green: 'from-safety-500 to-safety-600',
        purple: 'from-purple-500 to-purple-600',
        amber: 'from-amber-500 to-amber-600',
    };

    return (
        <div className="card-glass p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm text-safety-600 font-medium">{trend}</span>
            </div>
            <p className="text-3xl font-display font-bold text-charcoal-950 mb-1">{value}</p>
            <p className="text-sm text-steel-600">{label}</p>
        </div>
    );
}
